import { GetAppSettings } from '../AppSettings';

const routes = [
  // '/templates/:id', // template editor
];

// mutable bc we have to clear it and recreate it
// only allows one recording to be invoked at a time
let stopTimeout: ReturnType<typeof setTimeout> | null = null;

export function handleNavigateForRecording(route, _path) {
  if (!!routes.includes(route)) {
    startRecording();
  } else if (!!stopTimeout) {
    // only auto stop the recording on navigate if it was manually invoked
    // this keeps the auto recordings from being stopped
    stopRecording();
  }
}

export function initalizeScreenRecording() {
  initializeMixpanel();
}

function initializeMixpanel() {
  const appSettings = GetAppSettings();
  if (!appSettings.MixpanelApiKey || !window['mixpanel']?.init) {
    return;
  }

  try {
    window['mixpanel'].init(appSettings.MixpanelApiKey, {
      // Percentage of SDK initializations that will qualify for replay data capture. A value of “1” = 1%.
      record_sessions_percent: 1, //records 1% of all sessions, default: 0
      record_idle_timeout_ms: 1800000,

      // Minimum length of a single replay in milliseconds. Up to 8 seconds is supported. If a replay does not meet the minimum length, it will be discarded.
      // record_min_ms: 0,

      // Maximum length of a single replay in milliseconds. Up to 24 hours is supported. Once a replay has reached the maximum length, a new one will begin.
      record_max_ms: 600000, // 5 min. default: 86400000,

      // When true, Mixpanel will record snapshots of <canvas> elements on your site at up to 15 frames per second
      record_canvas: true, // default: false

      // When true, Mixpanel will capture click events during replays to populate Heatmaps. You can learn more here.
      record_heatmap_data: false, //default: false

      // The settings below control elements that will be obfuscated in the recording.
      // The commented values are the default values

      // CSS class name or regular expression for elements which will be replaced with an empty element of the same dimensions, blocking all contents.
      // record_block_class: new RegExp('^(mp-block|fs-exclude|amp-block|rr-block|ph-no-capture)$')

      // CSS class name or regular expression for elements that will have their text contents masked.
      record_mask_text_class: '', // new RegExp('^(mp-mask|fs-mask|amp-mask|rr-mask|ph-mask)$')

      // CSS selector for elements that will have their text contents masked.
      // The default will mask any text input
      record_mask_text_selector: '',

      // CSS selector for elements which will be replaced with an empty element of the same dimensions, blocking all contents.
      record_block_selector: 'video' //"img", "video"
      //autocapture: false // do not enable, this will cause our events to spike
    });
  } catch (err) {
    console.error(err);
  }
}

export function addScreenRecordingMiddleware() {
  if (!window['analytics'] || !window['mixpanel']) {
    return;
  }
  // Middleware to add Mixpanel's session recording properties to Segment events
  window['analytics'].addSourceMiddleware(({ payload, next, _integrations }) => {
    try {
      if (!window['mixpanel'] || !payload?.obj?.type || !payload?.obj?.properties) {
        next(payload);
        return;
      }

      if (payload.obj.type === 'track' || payload.obj.type === 'page') {
        const replayProps = window['mixpanel']?.get_session_recording_properties?.() || {};
        const hasActiveReplay = replayProps?.$mp_replay_id?.length > 0 || !!stopTimeout;

        if (hasActiveReplay) {
          payload.obj.properties = {
            ...payload.obj.properties,
            ...replayProps,
            hasRecording: true
          };
        }
      }
    } catch (e) {
      console.error('Failed to add mixpanel properties to segment', e);
    }
    next(payload);
  });
}

export function identifyScreenRecording(id, deviceId) {
  window['mixpanel']?.identify?.(id);
  // Original ID Merge
  if (!!deviceId) {
    window['mixpanel']?.register?.({ $device_id: deviceId });
  }
}
export function disableRecording() {
  // remove ability to record
  delete window['mixpanel'];
}

export function startRecording() {
  if (!window['mixpanel']?.start_session_recording) {
    return;
  }

  // ensure any current recording is stopped before starting a new one
  stopRecording();

  window['mixpanel'].start_session_recording();

  stopTimeout = setTimeout(() => {
    stopRecording(); // Auto-stop
  }, 5 * 60 * 1000); // 5 minutes in milliseconds
}

export function stopRecording() {
  if (!window['mixpanel']?.stop_session_recording || !stopTimeout) {
    return;
  }

  // cancel any auto cancel callbacks
  if (stopTimeout) {
    clearTimeout(stopTimeout);
    stopTimeout = null;
  }

  window['mixpanel'].stop_session_recording();
}
